import { QueryClient } from "@tanstack/react-query";
import { isRedirect, redirect } from "@tanstack/react-router";

import { getJoinStateQuery } from "@/modules/coaching/join/queries/getJoinStateQuery";
import { ROUTES } from "@/modules/shared/consts/routes";
import { ApiError } from "@/modules/shared/utils/ApiError";
import { captureApiError } from "@/modules/shared/utils/captureApiError";
import { DEFAULT_ENTRY_POINT } from "@/modules/tracking/consts/EntryPoints";
import { SHORT_URL_EVENTS } from "@/modules/tracking/consts/Events";
import {
  ShortUrlFlow,
  ShortUrlTerminalState,
} from "@/modules/tracking/consts/ShortUrl";
import { abandonPreviousShortUrlFlowIfDifferent } from "@/modules/tracking/utils/abandonPreviousShortUrlFlow";
import { completeShortUrlFlow } from "@/modules/tracking/utils/completeShortUrlFlow";
import {
  getShortUrlResolutionFromError,
  getShortUrlResolutionFromSuccess,
} from "@/modules/tracking/utils/getShortUrlResolutionStatus";
import { readSourceFromLocation } from "@/modules/tracking/utils/readSourceFromLocation";
import { registerSuperProperties } from "@/modules/tracking/utils/registerSuperProperties";
import {
  endShortUrlFlowSession,
  isSameShortUrlFlowVisit,
  startShortUrlFlowSession,
} from "@/modules/tracking/utils/shortUrlFlowSession";
import { trackShortUrlEvent } from "@/modules/tracking/utils/trackShortUrlEvent";

type BookSessionLoaderArgs = {
  context: { queryClient: QueryClient };
  params: { sessionCode: string };
};

export const bookSessionLoader = async ({
  context: { queryClient },
  params: { sessionCode },
}: BookSessionLoaderArgs) => {
  const source = readSourceFromLocation();
  const isRevalidation = isSameShortUrlFlowVisit(
    ShortUrlFlow.Book,
    sessionCode,
  );

  let data;
  try {
    data = await queryClient.ensureQueryData(getJoinStateQuery(sessionCode));
  } catch (error) {
    if (isRedirect(error)) throw error;

    if (!isRevalidation) {
      const resolution = getShortUrlResolutionFromError(error);
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: sessionCode,
        session_id: null,
        coach_id: null,
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: null,
      });
    }
    endShortUrlFlowSession();
    if (error instanceof ApiError) captureApiError(error);
    throw redirect({ to: ROUTES.notFound });
  }

  if (!data.bookNewSession) {
    if (!isRevalidation) {
      const resolution = getShortUrlResolutionFromSuccess();
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: sessionCode,
        session_id: null,
        coach_id: null,
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: data.correlationId ?? null,
      });
    } else {
      completeShortUrlFlow(ShortUrlTerminalState.BookingCompleted, {
        result: "error",
        extraProps: { coach_id: null, error_type: "no_session_available" },
      });
    }
    endShortUrlFlowSession();
    throw redirect({ to: ROUTES.notFound });
  }

  registerSuperProperties({
    entry_point: DEFAULT_ENTRY_POINT,
    customer_id: data.bookNewSession.customer.id,
  });

  if (!isRevalidation) {
    abandonPreviousShortUrlFlowIfDifferent(ShortUrlFlow.Book, sessionCode);

    const resolution = getShortUrlResolutionFromSuccess();
    trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
      access_code: sessionCode,
      session_id: null,
      coach_id: null,
      resolution_status: resolution.resolution_status,
      http_status: resolution.http_status,
      correlation_id: data.correlationId ?? null,
    });

    startShortUrlFlowSession({
      flow: ShortUrlFlow.Book,
      accessCode: sessionCode,
      sessionId: null,
      source,
      correlationId: data.correlationId ?? null,
    });

    trackShortUrlEvent(SHORT_URL_EVENTS.FLOW_ENTERED, {
      flow: ShortUrlFlow.Book,
      session_id: null,
      coach_id: data.bookNewSession.coach?.id ?? null,
      source,
    });
  }

  return { bookNewSession: data.bookNewSession };
};
